import { refreshAccessToken } from "../api/authApi";
import { getRefreshToken, saveTokens, clearTokens } from "./authStorage";

// Several requests can all get a 401 at the exact same moment — e.g. Profiles
// and Pricing both firing on page load with a token that expired overnight
// (exactly what the screenshot's logs show). We only want ONE
// /token/refresh/ call in flight at a time; every other failed request just
// waits on this same promise instead of racing its own refresh call.
let refreshPromise = null;

function getOrStartRefresh() {
  if (!refreshPromise) {
    const refresh = getRefreshToken();
    if (!refresh) {
      clearTokens();
      refreshPromise = Promise.reject(new Error("Not logged in."));
    } else {
      refreshPromise = refreshAccessToken(refresh)
        .then((data) => {
          saveTokens({ access: data.access, refresh: data.refresh });
          return data.access;
        })
        .catch((err) => {
          // Refresh token itself is invalid/expired (e.g. unused for 30+ days) —
          // there's no way back without a fresh OTP login, so log the user out.
          clearTokens();
          throw err;
        });
    }
    // Whether it succeeds or fails, clear the shared slot once it settles so
    // the *next* expired-token error starts a brand new refresh attempt
    // instead of reusing an already-settled promise.
    refreshPromise.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Attaches a response interceptor to an axios instance (kundliApi, walletApi,
 * poojaApi, ...) so that a 401 caused by an expired/invalid access token is
 * handled automatically:
 *   1. Silently refresh the access token (de-duplicated, see above).
 *   2. Retry the original request once with the new token.
 *   3. If the refresh itself fails (no refresh token, or it's expired too —
 *      e.g. an anonymous visitor or a stale localStorage token), retry ONE
 *      more time with no Authorization header at all. AllowAny endpoints
 *      (pooja list, kundli pricing, etc.) then succeed anonymously instead
 *      of wrongly bouncing the visitor with a 401. Endpoints that truly
 *      require login will 401 again on this anonymous retry, and that error
 *      is what finally surfaces to the caller.
 *   4. isLoggedIn() returns false once tokens are cleared, so the existing
 *      LoginModal flow (usePremiumGate) naturally asks the user to log in
 *      next time a gated action runs.
 *
 * Call this once per axios instance, right after axios.create(...).
 */
export function attachAuthRefresh(instance) {
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      // Only handle "expired/invalid token" 401s, and only retry once per
      // request (the _retry flag prevents an infinite loop if the retried
      // request somehow 401s again).
      if (status !== 401 || !original || original._retry) {
        return Promise.reject(error);
      }
      original._retry = true;

      try {
        const newAccess = await getOrStartRefresh();
        original.headers.Authorization = `Bearer ${newAccess}`;
        return instance(original);
      } catch {
        // No valid refresh token either (anonymous visitor, or a stale
        // token sitting in localStorage from a previous session). Don't
        // just surface the 401 — retry ONCE more with no Authorization
        // header at all. If the endpoint is AllowAny this succeeds
        // anonymously. If the endpoint truly requires login, it 401s again
        // and we let that through as the real "please login" error.
        if (!original._retriedAnonymous) {
          original._retriedAnonymous = true;
          delete original.headers.Authorization;
          try {
            return await instance(original);
          } catch (anonErr) {
            return Promise.reject(anonErr);
          }
        }
        return Promise.reject(error);
      }
    }
  );
}